import { useState, useContext } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate, Link } from 'react-router-dom';
import ThreeSixtyPayLogo from '../assets/images/360pay-grey-logo.png';
import ICS2Logo from '../assets/images/ics2-logo.png';
import { ShowIf, useSecurity } from '../components/Security/security';
import { CONTACT_US_URL } from '../utils/constants';
import { StoreContext } from '../components/Store';
import { isEORIUser, isITUser } from '../utils/roles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MaintenanceBanner from '../components/MaintenanceBanner';
import { SSOContext } from '../components/SSO/SSO';
import MoreAppsButton from '../components/MoreAppsButton/MoreAppsButton';
import LoadingSpinner from '../components/LoadingSpinner';
import PersonIcon from '@mui/icons-material/Person';
import Popover from '@mui/material/Popover';
import dayjs from 'dayjs';
import EoriValidationsModal from '../components/EoriValidations.js/EoriValidationsModal';

const navButtonStyle = {
  my: 2,
  mx: 1.5,
  color: '#000',
  display: 'block',
  fontSize: '17px',
  textTransform: 'none',
  fontWeight: '100',
  fontFamily: 'Roboto, sans-serif',
};

const userIconStyle = {
  display: 'flex',
  alignItems: 'center',
  mx: 3,
};

const UserInfoItem = ({ label, value }) => {
  if (!value) return null;

  return (
    <Box sx={{ my: 1 }}>
      <Typography variant="body2">
        <strong>{label}:</strong> {value}
      </Typography>
    </Box>
  );
};

const Navigation = ({ handleLogOut, handleNavigate, isSSOUserWithNoCompany }) => {
  const navigate = useNavigate();
  const [userInfoEl, setUserInfoEl] = useState(null);
  const [adminMenuEl, setAdminMenuEl] = useState(null);
  const adminMenuOpen = Boolean(adminMenuEl);

  const [eoriModalOpen, setEoriModalOpen] = useState(false);

  const handlePopoverOpen = (event) => {
    setUserInfoEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setUserInfoEl(null);
  };

  const getSessionStorageValue = (value) => {
    if (value === 'null' || value === 'undefined' || !value) return '';
    return value;
  };

  const showUserInfo = Boolean(userInfoEl);

  const userFullName = getSessionStorageValue(sessionStorage.getItem('fullName'));
  const userCompanyName = getSessionStorageValue(sessionStorage.getItem('company'));
  const userDepartment = getSessionStorageValue(sessionStorage.getItem('department'));
  const userRole = getSessionStorageValue(sessionStorage.getItem('role'));
  const userLastLogin = getSessionStorageValue(sessionStorage.getItem('lastLoggedIn'));

  return (
    <>
      <EoriValidationsModal show={eoriModalOpen} onHide={() => setEoriModalOpen(false)} />

      <ShowIf isLoggedIn>
        <Button key="Create ENS" onClick={() => navigate('/EnsDeclarationSelectCompany')} sx={navButtonStyle}>
          Request EU ENS
        </Button>

        <Button key="Dashboard" onClick={() => navigate('/Dashboard')} sx={navButtonStyle}>
          Dashboard
        </Button>

        <Button key="KB" onClick={() => window.open(process.env.REACT_APP_HUBSPOT_URL, '_blank')} sx={navButtonStyle}>
          Knowledge Base
        </Button>

        <Button key="contact us" onClick={() => window.open(`${CONTACT_US_URL}`, '_blank')} sx={navButtonStyle}>
          Contact Us
        </Button>

        {isITUser() && (
          <>
            <Button
              key="Admin"
              onClick={(event) => setAdminMenuEl(event.currentTarget)}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                ...navButtonStyle,
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
                '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
              }}
              aria-controls={adminMenuOpen ? 'admin-nav-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={adminMenuOpen ? 'true' : undefined}
            >
              Admin
            </Button>
            <Menu
              id="admin-nav-menu"
              anchorEl={adminMenuEl}
              open={adminMenuOpen}
              onClose={() => setAdminMenuEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => {
                  setAdminMenuEl(null);
                  navigate('/Admin/MaintenanceBanner');
                }}
              >
                Maintenance Banner
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAdminMenuEl(null);
                  setEoriModalOpen(true);
                }}
              >
                EORI Number Validation
              </MenuItem>
            </Menu>
          </>
        )}

        <Button key="Status" onClick={() => handleNavigate()} sx={navButtonStyle}>
          Settings
        </Button>

        <Button key="Logout" onClick={() => handleLogOut(false)} sx={navButtonStyle}>
          Log Out
        </Button>

        <Box sx={userIconStyle}>
          <Box
            aria-owns={showUserInfo ? 'user-info-popover' : undefined}
            aria-haspopup="true"
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
          >
            <PersonIcon sx={{ color: '#000', fontSize: 30 }} />
          </Box>

          <Popover
            id="user-info-popover"
            sx={{ pointerEvents: 'none' }}
            open={showUserInfo}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            onClose={handlePopoverClose}
            onMouseLeave={handlePopoverClose}
            anchorEl={userInfoEl}
          >
            <Box sx={{ p: 2 }}>
              <Box>
                <UserInfoItem label="Name" value={userFullName} />
                <UserInfoItem label="Company" value={userCompanyName} />
                {userDepartment !== 'Haulier' && <UserInfoItem label="Department" value={userDepartment} />}
                <UserInfoItem label="Role" value={userRole} />
                {dayjs(userLastLogin).isValid() && (
                  <UserInfoItem label="Last Login" value={dayjs(userLastLogin).format('DD/MM/YYYY HH:mm')} />
                )}
              </Box>
            </Box>
          </Popover>
        </Box>
      </ShowIf>

      <ShowIf isLoggedIn={false}>
        {!isSSOUserWithNoCompany ? (
          <Button key="Login" onClick={() => navigate('/login')} sx={{ ...navButtonStyle, fontSize: 20, mx: 0 }}>
            Login
          </Button>
        ) : (
          <Button key="Logout" onClick={(e) => handleLogOut(true)} sx={navButtonStyle}>
            Log Out
          </Button>
        )}
      </ShowIf>
    </>
  );
};

const Header = () => {
  const store = useContext(StoreContext);
  const sso = useContext(SSOContext);
  const navigate = useNavigate();
  const { dispatch, isLoggedIn } = useSecurity();
  const [isLoadingSSO] = store.isLoadingSSO;
  const [isSSOUserWithNoCompany] = store.isSSOUserWithNoCompany;

  const handleLogOut = async (SSOLogout) => {
    if (sessionStorage.getItem('sso') === '1' || SSOLogout) {
      sso.handleSSOLogout();
    }

    sessionStorage.removeItem('token');
    sessionStorage.removeItem(process.env.REACT_APP_API + 'userdata');
    dispatch({ type: 'logout' });
    navigate('/');
  };
  const handleNavigate = () => {
    if (isEORIUser()) {
      navigate('/SelectCustomer');
    } else {
      navigate('/CustomerSettings');
    }
  };

  const showMoreApps = sessionStorage.getItem('sso') === '1';

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: '#fff', color: '#000' }} className="header-component">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {showMoreApps && <MoreAppsButton />}
            <Box
              component={Link}
              to={isLoggedIn ? '/dashboard' : '/'}
              sx={{
                mr: 2,
                display: { md: 'flex' },
                textDecoration: 'none',
                alignItems: 'center',
              }}
            >
              <img
                src={ThreeSixtyPayLogo}
                alt="360pay Logo"
                style={{
                  height: '40px',
                  width: 'auto',
                  marginTop: 22,
                  marginBottom: 22,
                }}
              />
              <span style={{ display: 'block', color: '#000', paddingLeft: 20, paddingRight: 20 }}>Powered By</span>
              <img
                src={ICS2Logo}
                alt="CABIE ICS2 Logo"
                style={{
                  height: '50px',
                  width: 'auto',
                  marginTop: 17,
                  marginBottom: 17,
                }}
              />
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex', mr: '3' },
                marginTop: '4px',
                justifyContent: 'flex-end',
              }}
            >
              {isLoadingSSO ? (
                <LoadingSpinner />
              ) : (
                <Navigation
                  handleLogOut={handleLogOut}
                  handleNavigate={handleNavigate}
                  isSSOUserWithNoCompany={isSSOUserWithNoCompany}
                />
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <MaintenanceBanner />
    </>
  );
};

export default Header;
